<template>
  <div v-shortkey.stop="shortkeys" @shortkey="triggerShortkey" class="cytology-questions-container">
    <div class="questions-header">
      <h4>Clinical <u>Q</u>uestions</h4>
    </div>

    <div class="questions-content">
      <transition name="fade" mode="out-in">
        <!-- Loading skeleton -->
        <div v-if="isLoading" key="skeleton" class="questions-skeleton">
          <div v-for="n in skeletonCount" :key="n" class="skeleton-question">
            <div class="skeleton-label"></div>
            <div class="skeleton-input"></div>
          </div>
        </div>

        <!-- Questions content -->
        <transition-group
          v-else
          key="content"
          name="question-slide"
          tag="div"
          class="questions-wrapper"
          appear
        >
          <div
            class="question"
            v-for="(question, index) in questions"
            :key="question.id"
            :style="{ transitionDelay: `${index * 0.1}s` }"
          >
            <template v-if="question.id">
              <transition name="fade-fast" appear>
                <div>
                  <TextInput
                    ref="questionInputs"
                    v-if="question.cytQuestionType === 'Text'"
                    :label="question.displayText"
                    v-model="answerMap[question.id]"
                    :required="question.isResponseRequired"
                    type="text"
                  />
                  <DatePicker
                    v-else-if="question.cytQuestionType === 'Date'"
                    :label="question.displayText"
                    ref="questionInputs"
                    v-model="answerMap[question.id]"
                    :required="question.isResponseRequired"
                  />
                  <textarea
                    v-else-if="question.cytQuestionType === 'textarea'"
                    ref="questionInputs"
                    v-model="answerMap[question.id]"
                    :required="question.isResponseRequired"
                    class="form-control"
                  ></textarea>
                  <SelectInput
                    v-else-if="question.cytQuestionType === 'List'"
                    ref="questionInputs"
                    :label="question.displayText"
                    :items="question.options"
                    v-model="answerMap[question.id]"
                    :required="question.isResponseRequired"
                  />
                </div>
              </transition>
            </template>
          </div>
        </transition-group>
      </transition>
    </div>
  </div>
</template>

<script>
import { dateRangeFilter, getAltKeys, logShortKey } from "@/modules/helpers";
import cytologyService, { CytologyEntities } from "@/services/cytology";
import { mapState } from "vuex";
import DatePicker from "../common/DatePicker.vue";
import TextInput from "../common/TextInput.vue";
import SelectInput from "../common/SelectInput.vue";

export default {
  components: {
    DatePicker,
    TextInput,
    SelectInput
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    caseId: {
      required: true
    },
    autoFocus: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLoading: false,
      questionResponses: [],
      molecTests: [],
      cytologyQuestionsStore: cytologyService.createSearch(CytologyEntities.CytQuestions),
      labQuestions: {},
      shortkeys: getAltKeys("q")
    };
  },
  created() {
    this.loadLabQuestions();
  },
  methods: {
    triggerShortkey(event) {
      logShortKey(this.shortkeys[event.srcKey]);
      switch (event.srcKey) {
        case "q":
          this.focusFirstInput();
          break;
      }
    },
    focusFirstInput() {
      this.$nextTick(() => {
        const refs = this.$refs.questionInputs;
        if (refs && refs.length) {
          refs[0].focus();
        }
      });
    },
    async loadLabQuestions() {
      try {
        this.isLoading = true;
        const labQuestions = await this.cytologyQuestionsStore.load({
          filter: dateRangeFilter(),
          sort: [
            {
              selector: "seqNum",
              desc: false
            }
          ]
        });

        if (!labQuestions?.length) return;
        this.skeletonCount = labQuestions.length;
        for (const question of labQuestions) {
          if (question.cytQuestionType === "List") {
            const questionDetails = await cytologyService.getSingleEntity(
              CytologyEntities.CytQuestions,
              question.id
            );
            question.options = questionDetails.options;
          }
        }
        this.labQuestions = labQuestions.reduce((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {});
        if (this.autoFocus) {
          await this.focusFirstInput();
        }
        //! TODO: add error handling
      } finally {
        this.isLoading = false;
      }
    },
    setResponse(questionId, response) {
      this.answerMap[questionId] = response;
    },
    getQuestionResponse(questionId) {
      const response = this.questionResponses.find(q => q.questionId === questionId);
      return response ? response.response : "";
    }
  },
  computed: {
    ...mapState({
      specimens: state => state.accessionStore.caseDetails.specimens
    }),
    answerMap: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    skeletonCount: {
      get() {
        const localVal = localStorage.getItem("CYT_QUESTIONS_SKELETON_COUNT");
        if (!isNaN(Number(localVal))) {
          return parseInt(localVal, 10);
        }
        return localStorage.getItem("CYT_QUESTIONS_SKELETON_COUNT") || 6;
      },
      set(value) {
        localStorage.setItem("CYT_QUESTIONS_SKELETON_COUNT", value);
      }
    },
    existingResponses() {
      return this.questionResponses.reduce((acc, curr) => {
        acc[curr.cytQuestionId] = curr.id;
        return acc;
      }, {});
    },
    questions() {
      return Object.values(this.labQuestions).sort((a, b) => a.seqNum - b.seqNum);
    }
  }
};
</script>

<style lang="scss" scoped>
.cytology-questions-container {
  transition: all 0.3s ease;
}

.questions-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  min-height: 40px;
}

.questions-content {
  position: relative;
  min-height: 300px;
}

.questions-wrapper {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(8, auto);
  gap: 10px;
}

.question {
  transition: all 0.3s ease;
}

/* Skeleton loading styles - matches original layout */
.questions-skeleton {
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(8, auto);
  gap: 10px;
}

.skeleton-question {
  padding: 0.5rem 0;
}

.skeleton-label {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 2s infinite;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  width: 60%;
}

.skeleton-input {
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 2s infinite;
  border-radius: 4px;
  width: 100%;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Transition styles */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.2s ease;
}
.fade-fast-enter,
.fade-fast-leave-to {
  opacity: 0;
}

/* Question slide transitions */
.question-slide-enter-active {
  transition: all 0.4s ease;
}
.question-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
.question-slide-leave-active {
  transition: all 0.3s ease;
}
.question-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Ensure smooth layout during transitions */
.question {
  will-change: transform, opacity;
}

/* Grid styles for legacy support */
.questions-grid {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 1rem;
  .loader {
    align-self: center;
  }

  .text-input {
    min-height: fit-content;
  }
}

/* Textarea styling */
textarea.form-control {
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}
</style>
